import { Controller } from '@hotwired/stimulus';
import { get } from '@rails/request.js';

// The automated message and the interview script of a hiring step form.
//
// Both work the same way. A modal edits a value into a hidden input, and the
// step's own Save is what submits it. Only the modal's Save keeps an edit:
// closing it any other way puts back what was there when it opened. Closing
// the accordion item throws away everything staged.
//
// The message is a Ui::RichTextEditorComponent staging `message[text]`. An
// empty editor is left unsubmittable, so the interactors read "no message"
// (and destroy an existing one) rather than an invalid blank text. The add and
// edit wording of its buttons is server-rendered in both states, and this only
// reveals the one that matches.
//
// The script belongs to the landing screening kind alone. Its modal holds one
// draggable card per question, and Save stages them as JSON in
// `screening_script`.
export default class extends Controller {
  static targets = ['messageEditor', 'messageTriggerAdd', 'messageTriggerEdit', 'messageSubmitAdd',
    'messageSubmitEdit', 'messageDelete', 'stepFields', 'featureBanner',
    'scriptSection', 'scriptInput', 'scriptList', 'questionInput', 'questionTemplate'];

  static values = {
    messageTemplates: Object,
    messageKind: String,
  };

  connect() {
    // Everything the form can stage sits inside this element, so one snapshot
    // of it is all a reset needs. innerHTML copies attributes, not properties,
    // so nothing the user types or toggles later leaks into it.
    this.defaultHTML = this.element.innerHTML;

    // The picked kind is the exception: it lives on the form element itself,
    // which that snapshot does not cover.
    this.defaultKind = this.messageKindValue;

    // The editor's controller and Trix itself can come up in either order, so
    // check once more after both have had their chance. Otherwise an empty
    // message is left submittable.
    this.syncFrame = requestAnimationFrame(() => this.syncMessagePresence());
  }

  disconnect() {
    if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
    this.syncFrame = null;
  }

  // Closing the item, or opening another one, drops whatever was staged by
  // putting the server-rendered markup back. Every controller inside it
  // reconnects to that markup, Trix included: it rebuilds its editor from the
  // restored hidden input and our toolbar.
  resetForm(event) {
    if (!event.target.contains(this.element)) return;

    this.element.innerHTML = this.defaultHTML;
    this.messageKindValue = this.defaultKind;
    this.messageSnapshot = undefined;
    this.scriptSnapshot = undefined;
  }

  // Everything that depends on the kind is updated here, from the group's own
  // change event: the default message text, the script section, and the
  // feature banner that replaces the step fields when the company has no RPO
  // service. A staged script survives switching away and back.
  kindChanged(event) {
    const screening = event.detail.selected === 'landing_screening';

    this.messageKindValue = event.detail.selected;

    if (this.hasFeatureBannerTarget) {
      this.featureBannerTarget.hidden = !screening;
      this.stepFieldsTarget.hidden = screening;
    }

    if (!this.hasScriptSectionTarget) return;

    this.scriptSectionTarget.hidden = !screening;
    this.scriptInputTarget.disabled = !screening || !this.scriptInputTarget.value;
  }

  // ── Automated message ───────────────────────────────────────────────────────

  openMessage(event) {
    if (!this.inMessageModal(event)) return;

    this.messageSnapshot = this.editor?.html;
    if (this.editor?.blank) this.editor.load(this.messageTemplatesValue[this.messageKindValue]);
  }

  saveMessage() {
    this.editor?.commit();
    this.messageSnapshot = this.editor?.html;
    this.syncMessagePresence();
    this.syncMessageControls();
  }

  // Trix writes the editor into the hidden input as you type, so every way of
  // dismissing the modal has to put the snapshot back. Without this, a
  // template nobody confirmed would submit with the step. Save and Delete take
  // their new state as the snapshot before this runs.
  closeMessage(event) {
    if (!this.inMessageModal(event)) return;
    if (this.messageSnapshot === undefined) return;

    this.editor?.load(this.messageSnapshot);
  }

  deleteMessage() {
    this.editor?.load('');
    this.messageSnapshot = '';
    this.syncMessageControls();
  }

  syncMessageControls() {
    const blank = this.editor?.blank ?? true;

    this.messageTriggerAddTarget.hidden = !blank;
    this.messageTriggerEditTarget.hidden = blank;
    this.messageSubmitAddTarget.hidden = !blank;
    this.messageSubmitEditTarget.hidden = blank;
    this.messageDeleteTarget.hidden = blank;
  }

  // Wired to trix-initialize and trix-change.
  syncMessagePresence() {
    if (this.editor) this.editor.submittable = !this.editor.blank;
  }

  get editor() {
    if (!this.hasMessageEditorTarget) return null;

    return this.application.getControllerForElementAndIdentifier(
      this.messageEditorTarget,
      'rich-text-editor-component'
    );
  }

  // ── Interview script ────────────────────────────────────────────────────────

  // The banner's "Add feature" button: asks our team to get in touch about the
  // RPO service, once, as the old FeatureCta did.
  async requestScreeningFeature(event) {
    const button = event.target.closest('button');
    button.disabled = true;

    const response = await get(event.params.url, { responseKind: 'json' });
    if (response.ok) {
      button.querySelector('.ui-button__text').textContent = 'Request sent';
    } else {
      button.disabled = false;
    }
  }

  openScript(event) {
    if (!this.inScriptModal(event)) return;

    this.scriptSnapshot = this.scriptListTarget.innerHTML;
    this.removedQuestions = [];
  }

  addQuestion(event) {
    event.preventDefault();
    const input = this.questionInputTarget;
    const text = input.value.trim();
    if (!text) return input.focus();

    const card = this.questionTemplateTarget.content.firstElementChild.cloneNode(true);
    card.id = `${this.element.id}-question-${Date.now()}`;
    card.querySelector('.ui-draggable-card__title').textContent = text;
    this.scriptListRoot.appendChild(card);

    input.value = '';
    input.focus();
  }

  removeQuestion(event) {
    const card = event.target.closest('.ui-draggable-card');
    this.removedQuestions.push({ card, index: Array.from(card.parentElement.children).indexOf(card) });
    card.remove();
  }

  // Ctrl/Cmd+Z puts the last removed question back where it was, which is what
  // the modal's hint promises. Only while that modal is open, and never while
  // the user is typing — there the keystroke is their own text undo.
  undoQuestion(event) {
    if (!(event.ctrlKey || event.metaKey) || event.key?.toLowerCase() !== 'z') return;
    if (event.target.closest('input, textarea, [contenteditable]')) return;
    if (!this.hasScriptListTarget || !this.removedQuestions?.length) return;

    const modal = this.scriptListTarget.closest('.ui-modal-backdrop');
    if (!modal || modal.hidden) return;

    event.preventDefault();
    const { card, index } = this.removedQuestions.pop();
    this.scriptListRoot.insertBefore(card, this.scriptListRoot.children[index] || null);
  }

  // An empty script stages nothing. The input stays disabled, and the server
  // keeps the script the job already has.
  saveScript() {
    const questions = Array.from(this.scriptListTarget.querySelectorAll('.ui-draggable-card__title'))
      .map((title) => title.textContent.trim())
      .filter(Boolean);

    this.scriptInputTarget.value = questions.length ? JSON.stringify(questions) : '';
    this.scriptInputTarget.disabled = questions.length === 0;
    this.scriptSnapshot = this.scriptListTarget.innerHTML;
  }

  // Same deal as closeMessage: dismissing reverts to the snapshot, which Save
  // updated just before the modal closed.
  closeScript(event) {
    if (!this.inScriptModal(event)) return;
    if (this.scriptSnapshot === undefined) return;

    this.scriptListTarget.innerHTML = this.scriptSnapshot;
  }

  get scriptListRoot() {
    return this.scriptListTarget.querySelector('.ui-draggable-list');
  }

  // Both modals are in the same form, so their open and close events reach
  // both pairs of handlers. Each one answers only for its own modal.
  inMessageModal(event) {
    return this.hasMessageEditorTarget && event.target.contains(this.messageEditorTarget);
  }

  inScriptModal(event) {
    return this.hasScriptListTarget && event.target.contains(this.scriptListTarget);
  }
}
